import type { RefObject } from 'react'
import type { ReadAloudPhase } from '../hooks/useReadAloudPolly'

type ReadAloudBarProps = {
  phase: ReadAloudPhase
  error: string | null
  canReplay: boolean
  onPlay: () => void
  onPause: () => void
  onStop: () => void
  audioRef: RefObject<HTMLAudioElement | null>
  /** Shown before the buttons (default: “Read aloud”). */
  barLabel?: string
}

/**
 * Compact Polly read-aloud controls (Play / Pause / Stop) above the chat input.
 */
export function ReadAloudBar(props: ReadAloudBarProps) {
  const { phase, error, canReplay, onPlay, onPause, onStop, audioRef, barLabel = 'Read aloud' } = props

  const busy = phase === 'loading'
  const playing = phase === 'playing'
  const paused = phase === 'paused'
  const idle = phase === 'idle'

  const showPlay = !playing && (paused || (idle && canReplay))
  const showPause = playing

  return (
    <div className="vnw-readAloudBar" aria-label={barLabel}>
      <audio ref={audioRef} preload="none" />
      <span className="vnw-readAloudBarLabel">{barLabel}</span>
      <div className="vnw-readAloudBarBtns">
        {showPlay ? (
          <button
            type="button"
            className="vnw-readAloudBtn"
            onClick={(e) => {
              e.stopPropagation()
              onPlay()
            }}
            disabled={busy || (!canReplay && !paused)}
            aria-label="Play"
            title="Play"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          </button>
        ) : null}
        {showPause ? (
          <button
            type="button"
            className="vnw-readAloudBtn"
            onClick={(e) => {
              e.stopPropagation()
              onPause()
            }}
            disabled={busy}
            aria-label="Pause"
            title="Pause"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            </svg>
          </button>
        ) : null}
        <button
          type="button"
          className="vnw-readAloudBtn vnw-readAloudBtn--stop"
          onClick={(e) => {
            e.stopPropagation()
            onStop()
          }}
          disabled={idle && !canReplay}
          aria-label="Stop"
          title="Stop"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M6 6h12v12H6z" />
          </svg>
        </button>
      </div>
      {busy ? <span className="vnw-readAloudBarStatus">Synthesizing…</span> : null}
      {error ? (
        <span className="vnw-readAloudBarError" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  )
}
